import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  useCircuits,
  useDocumentsServeis,
  usePlantilles,
  type ServeiAdminDto,
} from '@/api/administracio';
import { useDesaNode, type RecursEstructura } from '@/api/ajustosEstructura';
import { nomUsuari, useExpertsServeis, useUsuaris } from '@/api/planificacio';
import type { Canal, TipusArea } from '@/api/types';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Avatar, BadgeArea, Buit, Camp } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import { useMe } from '@/permisos/MeContext';
import { EditaExperts } from '@/pages/planificacio/W16Experts';
import { BarraCanvis } from '../marc/canvis';
import { Confirmacio } from '../marc/Calaix';
import type { AreaEstructura, DepartamentEstructura, TipusNode } from '../marc/estructura';
import { ErrorDesa } from '../comu';
import { badgeEstatDocument } from '../utils';
import { W34PujaDocument } from '../W34PujaDocument';
import { MouNode } from './Calaixos';
import { CanalsChips, FitxaCap } from './comu';
import { GuardiaArea, GuardiaDepartament, GuardiaServei } from './GuardiaAra';
import { LlistaPersonesInformar } from './LlistaPersonesInformar';
import { campsCanviats, type Seleccio } from './suport';

export interface AccionsFitxa {
  onSelecciona: (s: Seleccio) => void;
  onNou: (tipus: TipusNode, pareId: number) => void;
}

/** Treu les llistes de fills que l'arbre afegeix al DTO abans d'enviar-lo al servidor. */
function net<T extends object>(n: T): T {
  const c = { ...n } as Record<string, unknown>;
  delete c.arees;
  delete c.serveis;
  return c as T;
}

/**
 * Edició d'un node: formulari local, nombre de canvis respecte de l'original, desar amb ETag i
 * activar/desactivar. Els canvis es comparen amb les dades actuals de la llista.
 */
function useEditorNode<T extends { id?: number | null; actiu?: boolean }>(
  recurs: RecursEstructura,
  original: T,
  camps: (keyof T)[],
) {
  const [form, setForm] = useState<T>(original);
  const desa = useDesaNode<T>(recurs, original.id);
  const n = campsCanviats(original, form, camps);
  const editat = { ...net(original), ...Object.fromEntries(camps.map((c) => [c, form[c]])) } as T;
  return {
    form,
    set: (p: Partial<T>) => setForm((f) => ({ ...f, ...p })),
    n,
    desa,
    guarda: () => desa.mutate(editat),
    descarta: () => setForm(original),
    commutaActiu: (onFet: () => void) =>
      desa.mutate({ ...net(original), actiu: original.actiu === false } as T, { onSuccess: onFet }),
  };
}

/** Botó «Desactiva/Reactiva» amb confirmació. */
function BotoActiu({
  nom,
  actiu,
  ambFills,
  ocupat,
  onConfirma,
}: {
  nom: string;
  actiu: boolean;
  ambFills?: boolean;
  ocupat: boolean;
  onConfirma: (fet: () => void) => void;
}) {
  const { t } = useTranslation('administracio');
  const [obert, setObert] = useState(false);
  return (
    <Permis recurs="cai.organitzacio" tipus="MODIFICAR">
      <button
        type="button"
        className={`btn btn--s ${actiu ? 'btn--danger' : 'btn--outline'}`}
        onClick={() => setObert(true)}
      >
        <Icona nom={actiu ? 'power' : 'arrow-clockwise'} />
        {actiu ? t('estructura.accions.desactiva') : t('estructura.accions.reactiva')}
      </button>
      {obert ? (
        <Confirmacio
          titol={
            actiu
              ? t('estructura.confirma.desactivaTitol', { nom })
              : t('estructura.confirma.reactivaTitol', { nom })
          }
          textAccio={actiu ? t('estructura.confirma.desactiva') : t('estructura.confirma.reactiva')}
          perill={actiu}
          ocupat={ocupat}
          onTanca={() => setObert(false)}
          onConfirma={() => onConfirma(() => setObert(false))}
        >
          <p>
            {actiu ? t('estructura.confirma.desactivaText') : t('estructura.confirma.reactivaText')}
          </p>
          {actiu && ambFills ? (
            <p className="dim">{t('estructura.confirma.desactivaFills')}</p>
          ) : null}
        </Confirmacio>
      ) : null}
    </Permis>
  );
}

function BadgesNode({
  tipus,
  area,
  actiu,
}: {
  tipus: TipusNode;
  area?: TipusArea;
  actiu: boolean;
}) {
  const { t } = useTranslation('administracio');
  return (
    <>
      <span className="badge badge--gray">{t(`estructura.tipusCap.${tipus}`)}</span>
      {area ? <BadgeArea tipus={area} /> : null}
      {!actiu ? <span className="badge badge--incident">{t('estructura.inactiu')}</span> : null}
    </>
  );
}

/** Targetes dels fills (àrees d'un departament, serveis d'una àrea) + targeta per crear-ne. */
function Fills({
  titol,
  buit,
  tipusFill,
  fills,
  onSelecciona,
  onNou,
}: {
  titol: string;
  buit: string;
  tipusFill: 'AREA' | 'SERVEI';
  fills: {
    id: number;
    nom: string;
    codi?: string | null;
    actiu?: boolean;
    detall?: string;
    tipus?: TipusArea;
  }[];
  onSelecciona: (s: Seleccio) => void;
  onNou: () => void;
}) {
  const { t } = useTranslation('administracio');
  const { pot } = useMe();
  return (
    <div className="panel">
      <div className="panel__head">
        <h3>{titol}</h3>
        <span className="t-caption-2 dim">{fills.length}</span>
      </div>
      <div className="panel__body">
        {fills.length === 0 && !pot('cai.organitzacio', 'ALTA') ? (
          <p className="dim t-footnote" style={{ margin: 0 }}>
            {buit}
          </p>
        ) : (
          <div className="est-targetes">
            {fills.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`est-targeta${f.actiu === false ? ' is-inactiu' : ''}`}
                onClick={() => onSelecciona({ tipus: tipusFill, id: f.id })}
              >
                <span className="est-targeta__fila">
                  {f.tipus ? (
                    <span
                      className={`node-punt node-punt--${f.tipus.toLowerCase()}`}
                      aria-hidden="true"
                    />
                  ) : null}
                  <strong className="truncate">{f.nom}</strong>
                  {f.actiu === false ? (
                    <span className="badge badge--incident">{t('estructura.inactiu')}</span>
                  ) : null}
                </span>
                <span className="est-targeta__sub mono">
                  {[f.codi, f.detall].filter(Boolean).join(' · ') || '—'}
                </span>
              </button>
            ))}
            <Permis recurs="cai.organitzacio" tipus="ALTA">
              <button type="button" className="est-targeta est-targeta--nova" onClick={onNou}>
                <Icona nom="plus" />
                {t(`estructura.accions.nou.${tipusFill}`)}
              </button>
            </Permis>
          </div>
        )}
      </div>
    </div>
  );
}

/** Camps nom + codi d'un node. */
function CampsNomCodi({
  nom,
  codi,
  disabled,
  onCanvia,
}: {
  nom: string;
  codi?: string | null;
  disabled: boolean;
  onCanvia: (p: { nom?: string; codi?: string }) => void;
}) {
  const { t } = useTranslation('administracio');
  return (
    <div className="grid grid-2" style={{ gap: 12 }}>
      <Camp etiqueta={t('estructura.camps.nom')}>
        <input
          className="input"
          value={nom}
          disabled={disabled}
          onChange={(e) => onCanvia({ nom: e.target.value })}
        />
      </Camp>
      <Camp etiqueta={t('estructura.camps.codi')}>
        <input
          className="input mono"
          value={codi ?? ''}
          disabled={disabled}
          onChange={(e) => onCanvia({ codi: e.target.value.toUpperCase() })}
        />
      </Camp>
    </div>
  );
}

function Seccio({ titol, children }: { titol: string; children: ReactNode }) {
  return (
    <div className="panel">
      <div className="panel__head">
        <h3>{titol}</h3>
      </div>
      <div className="panel__body form">{children}</div>
    </div>
  );
}

export function FitxaDepartament({
  departament,
  onSelecciona,
  onNou,
}: AccionsFitxa & { departament: DepartamentEstructura }) {
  const { t } = useTranslation('administracio');
  const { pot } = useMe();
  const ed = useEditorNode('departaments', departament, ['nom', 'codi']);
  const editable = pot('cai.organitzacio', 'MODIFICAR');
  const actiu = departament.actiu !== false;
  const nServeis = departament.arees.reduce((n, a) => n + a.serveis.length, 0);
  return (
    <div className="lf__fitxa" data-fitxa="DEPARTAMENT" data-id={departament.id}>
      <FitxaCap
        ruta={t('estructura.organitzacio')}
        titol={departament.nom}
        badges={<BadgesNode tipus="DEPARTAMENT" actiu={actiu} />}
      >
        <Permis recurs="cai.organitzacio" tipus="ALTA">
          <button
            type="button"
            className="btn btn--dark btn--s"
            onClick={() => onNou('AREA', departament.id)}
          >
            <Icona nom="plus" />
            {t('estructura.accions.nou.AREA')}
          </button>
        </Permis>
        <BotoActiu
          nom={departament.nom}
          actiu={actiu}
          ambFills={departament.arees.length > 0}
          ocupat={ed.desa.isPending}
          onConfirma={ed.commutaActiu}
        />
      </FitxaCap>
      <div className="fitxa-cos">
        <GuardiaDepartament departament={departament} />
        <Seccio titol={t('estructura.dades.DEPARTAMENT')}>
          <CampsNomCodi
            nom={ed.form.nom}
            codi={ed.form.codi}
            disabled={!editable}
            onCanvia={ed.set}
          />
          <span className="dim t-footnote">
            {t('estructura.nArees', { count: departament.arees.length })} ·{' '}
            {t('estructura.nServeis', { count: nServeis })}
          </span>
          <ErrorDesa error={ed.desa.error} />
        </Seccio>
        <Fills
          titol={t('estructura.fills.AREA', { nom: departament.nom })}
          buit={t('estructura.senseFills.AREA')}
          tipusFill="AREA"
          fills={departament.arees.map((a) => ({
            ...a,
            detall: t('estructura.nServeis', { count: a.serveis.length }),
          }))}
          onSelecciona={onSelecciona}
          onNou={() => onNou('AREA', departament.id)}
        />
      </div>
      <BarraCanvis
        n={ed.n}
        nom={departament.nom}
        desant={ed.desa.isPending}
        desactivat={!ed.form.nom.trim()}
        onDesa={ed.guarda}
        onDescarta={ed.descarta}
      />
    </div>
  );
}

export function FitxaArea({
  area,
  departament,
  onSelecciona,
  onNou,
}: AccionsFitxa & { area: AreaEstructura; departament?: DepartamentEstructura }) {
  const { t } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const { pot } = useMe();
  const ed = useEditorNode('arees', area, ['nom', 'codi', 'tipus', 'descripcio']);
  const [moure, setMoure] = useState(false);
  const editable = pot('cai.organitzacio', 'MODIFICAR');
  const actiu = area.actiu !== false;
  return (
    <div className="lf__fitxa" data-fitxa="AREA" data-id={area.id}>
      <FitxaCap
        ruta={departament?.nom ?? '—'}
        titol={area.nom}
        badges={<BadgesNode tipus="AREA" area={area.tipus} actiu={actiu} />}
      >
        <Permis recurs="cai.organitzacio" tipus="ALTA">
          <button
            type="button"
            className="btn btn--dark btn--s"
            onClick={() => onNou('SERVEI', area.id)}
          >
            <Icona nom="plus" />
            {t('estructura.accions.nou.SERVEI')}
          </button>
        </Permis>
        <Permis recurs="cai.organitzacio" tipus="MODIFICAR">
          <button type="button" className="btn btn--outline btn--s" onClick={() => setMoure(true)}>
            <Icona nom="arrow-swap" />
            {t('estructura.accions.mou')}
          </button>
        </Permis>
        <BotoActiu
          nom={area.nom}
          actiu={actiu}
          ambFills={area.serveis.length > 0}
          ocupat={ed.desa.isPending}
          onConfirma={ed.commutaActiu}
        />
      </FitxaCap>
      <div className="fitxa-cos">
        <GuardiaArea area={area} />
        <Seccio titol={t('estructura.dades.AREA')}>
          <CampsNomCodi
            nom={ed.form.nom}
            codi={ed.form.codi}
            disabled={!editable}
            onCanvia={ed.set}
          />
          <div className="camp">
            <span className="camp__label">{t('estructura.camps.tipusArea')}</span>
            <div className="segmented" role="group" aria-label={t('estructura.camps.tipusArea')}>
              {(['GUARDIA_IT', 'CRISI'] as TipusArea[]).map((tp) => (
                <button
                  key={tp}
                  type="button"
                  aria-pressed={ed.form.tipus === tp}
                  disabled={!editable}
                  onClick={() => ed.set({ tipus: tp })}
                >
                  {tc(`tipusArea.${tp}`)}
                </button>
              ))}
            </div>
            <span className="camp__ajuda">{t(`estructura.ajudaTipus.${ed.form.tipus}`)}</span>
          </div>
          <Camp etiqueta={t('estructura.camps.descripcio')}>
            <textarea
              className="input"
              rows={2}
              value={ed.form.descripcio ?? ''}
              disabled={!editable}
              onChange={(e) => ed.set({ descripcio: e.target.value })}
            />
          </Camp>
          <ErrorDesa error={ed.desa.error} />
        </Seccio>
        <Fills
          titol={t('estructura.fills.SERVEI', { nom: area.nom })}
          buit={t('estructura.senseFills.SERVEI')}
          tipusFill="SERVEI"
          fills={area.serveis}
          onSelecciona={onSelecciona}
          onNou={() => onNou('SERVEI', area.id)}
        />
      </div>
      <BarraCanvis
        n={ed.n}
        nom={area.nom}
        desant={ed.desa.isPending}
        desactivat={!ed.form.nom.trim()}
        onDesa={ed.guarda}
        onDescarta={ed.descarta}
      />
      {moure ? (
        <MouNode
          tipus="AREA"
          node={net(area)}
          pareActual={departament?.nom ?? '—'}
          onTanca={() => setMoure(false)}
        />
      ) : null}
    </div>
  );
}

type PestanyaServei = 'general' | 'avisos' | 'persones' | 'documents';

/** Els circuits porten `actiu` des del backend; sense el camp es consideren actius. */
const circuitActiu = (c: object) => (c as { actiu?: boolean }).actiu !== false;

function Experts({ serveiId, serveiNom }: { serveiId: number; serveiNom: string }) {
  const { t } = useTranslation('administracio');
  const experts = useExpertsServeis([serveiId])[0]?.data ?? [];
  const usuaris = useUsuaris();
  const [editant, setEditant] = useState(false);
  const nom = (id: number) => {
    const u = usuaris.data?.find((x) => x.id === id);
    return u ? nomUsuari(u) : `#${id}`;
  };
  return (
    <div className="panel">
      <div className="panel__head">
        <h3>{t('estructura.experts.titol')}</h3>
        <div className="spacer" />
        <Permis recurs="cai.experts" tipus="MODIFICAR">
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={() => setEditant(true)}
          >
            <Icona nom="pencil" />
            {t('estructura.experts.gestiona')}
          </button>
        </Permis>
      </div>
      {editant ? (
        <EditaExperts
          servei={{ id: serveiId, nom: serveiNom }}
          experts={experts}
          onTanca={() => setEditant(false)}
        />
      ) : null}
      <div className="panel__body">
        {experts.length === 0 ? (
          <p className="dim t-footnote" style={{ margin: 0 }}>
            {t('estructura.experts.buit')}
          </p>
        ) : (
          <div className="est-guardia__persones">
            {experts.map((e) => {
              const n = e.nom ?? nom(e.usuariId);
              return (
                <div key={`${e.tipus}-${e.usuariId}`} className="est-persona">
                  <Avatar nom={n} color="bg-orange" />
                  <div className="est-persona__text">
                    <strong>{n}</strong>
                    <span>
                      {t(`estructura.experts.${e.tipus}`)}
                      {e.especialitat ? ` · ${e.especialitat}` : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Documents({ serveiId }: { serveiId: number }) {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const docs = useDocumentsServeis([serveiId]);
  const [puja, setPuja] = useState(false);
  const llista = docs.data ?? [];
  return (
    <div className="panel">
      <div className="panel__head">
        <h3>{t('estructura.documents.titol')}</h3>
        <div className="spacer" />
        <Link to="/admin/documentacio" className="btn btn--ghost btn--xs">
          {t('estructura.documents.biblioteca')}
        </Link>
        <Permis recurs="cai.documents" tipus="ALTA">
          <button type="button" className="btn btn--dark btn--xs" onClick={() => setPuja(true)}>
            <Icona nom="arrow-up" />
            {t('estructura.documents.puja')}
          </button>
        </Permis>
      </div>
      {llista.length === 0 ? (
        <Buit icona="doc-text">{docs.isLoading ? '…' : t('estructura.documents.buit')}</Buit>
      ) : (
        <div className="est-taula-scroll">
          <table className="taula">
            <tbody>
              {llista.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link to={`/admin/documentacio/${d.id}`} className="strong">
                      {d.titol}
                    </Link>
                    <div className="mono t-caption dim">{d.codi ?? `#${d.id}`}</div>
                  </td>
                  <td className="mono t-caption">
                    {d.versioVigent
                      ? t('estructura.documents.versio', { n: d.versioVigent.versio })
                      : '—'}
                  </td>
                  <td className="dim t-caption">{d.updatedAt ? f.data(d.updatedAt) : '—'}</td>
                  <td>
                    <span className={`badge ${badgeEstatDocument(d.estat)}`}>
                      {t(`documents.estats.${d.estat}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {puja ? <W34PujaDocument serveiId={serveiId} onTanca={() => setPuja(false)} /> : null}
    </div>
  );
}

export function FitxaServei({
  servei,
  area,
  departament,
}: AccionsFitxa & {
  servei: ServeiAdminDto;
  area?: AreaEstructura;
  departament?: DepartamentEstructura;
}) {
  const { t } = useTranslation('administracio');
  const { pot } = useMe();
  const original = { canalsDefecte: [] as Canal[], ...servei };
  const ed = useEditorNode('serveis', original, [
    'nom',
    'codi',
    'descripcio',
    'procedimentAlternatiu',
    'circuitEscalatId',
    'plantillaId',
    'canalsDefecte',
  ]);
  const plantilles = usePlantilles();
  const circuits = useCircuits();
  const [pestanya, setPestanya] = useState<PestanyaServei>('general');
  const [moure, setMoure] = useState(false);
  const editable = pot('cai.organitzacio', 'MODIFICAR');
  const actiu = servei.actiu !== false;
  const ruta = [departament?.nom, area?.nom].filter(Boolean).join(' › ');
  const pestanyes: PestanyaServei[] = ['general', 'avisos', 'persones', 'documents'];
  const circuitActual = (circuits.data ?? []).find((c) => c.id === ed.form.circuitEscalatId);

  return (
    <div
      className="lf__fitxa"
      data-fitxa="SERVEI"
      data-id={servei.id}
      data-screen={area?.tipus === 'CRISI' ? 'W-36' : undefined}
    >
      <FitxaCap
        ruta={ruta}
        titol={servei.nom}
        badges={<BadgesNode tipus="SERVEI" area={area?.tipus} actiu={actiu} />}
      >
        <Permis recurs="cai.organitzacio" tipus="MODIFICAR">
          <button type="button" className="btn btn--outline btn--s" onClick={() => setMoure(true)}>
            <Icona nom="arrow-swap" />
            {t('estructura.accions.mou')}
          </button>
        </Permis>
        <BotoActiu
          nom={servei.nom}
          actiu={actiu}
          ocupat={ed.desa.isPending}
          onConfirma={ed.commutaActiu}
        />
      </FitxaCap>
      <div
        className="fitxa-pestanyes"
        role="tablist"
        aria-label={t('estructura.pestanyes.etiqueta')}
      >
        {pestanyes.map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={pestanya === p}
            onClick={() => setPestanya(p)}
          >
            {t(`estructura.pestanyes.${p}`)}
          </button>
        ))}
      </div>
      <div className="fitxa-cos" role="tabpanel">
        {pestanya === 'general' ? (
          <Seccio titol={t('estructura.dades.SERVEI')}>
            <CampsNomCodi
              nom={ed.form.nom}
              codi={ed.form.codi}
              disabled={!editable}
              onCanvia={ed.set}
            />
            <Camp etiqueta={t('estructura.camps.descripcio')}>
              <textarea
                className="input"
                rows={2}
                value={ed.form.descripcio ?? ''}
                disabled={!editable}
                onChange={(e) => ed.set({ descripcio: e.target.value })}
              />
            </Camp>
            <Camp etiqueta={t('estructura.camps.procediment')}>
              <textarea
                className="input"
                rows={3}
                value={ed.form.procedimentAlternatiu ?? ''}
                disabled={!editable}
                onChange={(e) => ed.set({ procedimentAlternatiu: e.target.value })}
              />
            </Camp>
            <span className="camp__ajuda">{t('estructura.camps.procedimentAjuda')}</span>
          </Seccio>
        ) : null}
        {pestanya === 'avisos' ? (
          <Seccio titol={t('estructura.pestanyes.avisos')}>
            <div className="grid grid-2" style={{ gap: 12 }}>
              <div className="camp">
                <span className="camp__label">{t('estructura.camps.circuit')}</span>
                <Desplegable<number | ''>
                  aria-label={t('estructura.camps.circuit')}
                  valor={ed.form.circuitEscalatId ?? ''}
                  disabled={!editable}
                  opcions={[
                    { valor: '', etiqueta: t('estructura.camps.circuitDefecte') },
                    // Només circuits actius, més l'assignat ara (marcat inactiu) perquè no desaparegui.
                    ...(circuits.data ?? [])
                      .filter(
                        (c) =>
                          c.id != null &&
                          (circuitActiu(c) ||
                            c.id === servei.circuitEscalatId ||
                            c.id === ed.form.circuitEscalatId),
                      )
                      .map((c) => ({
                        valor: c.id as number,
                        etiqueta: c.nom,
                        detall: circuitActiu(c)
                          ? t('estructura.nNivells', { count: c.nivells.length })
                          : t('estructura.inactiu'),
                      })),
                  ]}
                  onCanvia={(v) => ed.set({ circuitEscalatId: v === '' ? null : Number(v) })}
                />
                {circuitActual ? (
                  <Link to="/admin/circuits" className="camp__ajuda est-enllac">
                    {t('estructura.camps.veuCircuit')}
                  </Link>
                ) : null}
              </div>
              <div className="camp">
                <span className="camp__label">{t('estructura.camps.plantilla')}</span>
                <Desplegable<number | ''>
                  aria-label={t('estructura.camps.plantilla')}
                  valor={ed.form.plantillaId ?? ''}
                  disabled={!editable}
                  opcions={[
                    { valor: '', etiqueta: t('estructura.camps.sensePlantilla') },
                    ...(plantilles.data ?? [])
                      .filter((p) => p.id != null && (p.actiu || p.id === ed.form.plantillaId))
                      .map((p) => ({
                        valor: p.id as number,
                        etiqueta: p.traduccions?.ca?.titol || p.codi,
                        detall: p.codi,
                      })),
                  ]}
                  onCanvia={(v) => ed.set({ plantillaId: v === '' ? null : Number(v) })}
                />
              </div>
            </div>
            <div className="camp">
              <span className="camp__label">{t('estructura.camps.canals')}</span>
              <CanalsChips
                etiqueta={t('estructura.camps.canals')}
                valor={ed.form.canalsDefecte ?? []}
                disabled={!editable}
                onCanvia={(canalsDefecte) => ed.set({ canalsDefecte })}
              />
              <span className="camp__ajuda">{t('estructura.camps.canalsAjuda')}</span>
            </div>
          </Seccio>
        ) : null}
        {pestanya === 'persones' ? (
          <>
            <div className="admin-2iguals">
              {area ? <GuardiaServei area={area} /> : null}
              <Experts serveiId={servei.id} serveiNom={servei.nom} />
            </div>
            <LlistaPersonesInformar serveiId={servei.id} />
          </>
        ) : null}
        {pestanya === 'documents' ? <Documents serveiId={servei.id} /> : null}
        <ErrorDesa error={ed.desa.error} />
      </div>
      <BarraCanvis
        n={ed.n}
        nom={servei.nom}
        desant={ed.desa.isPending}
        desactivat={!ed.form.nom.trim()}
        onDesa={ed.guarda}
        onDescarta={ed.descarta}
      />
      {moure ? (
        <MouNode
          tipus="SERVEI"
          node={net(servei)}
          pareActual={ruta || '—'}
          onTanca={() => setMoure(false)}
        />
      ) : null}
    </div>
  );
}
